import { useEffect, useState } from "react";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./form";
import { Input } from "./input";
import PhoneInput from "react-phone-input-2";
import { displayMoney, formatMoney } from "../utils/strings";
import { Switch } from "./switch";
import { Select } from "./select";
import { isObject } from "lodash";
import { Textarea } from "./textarea";
import { cn } from "../cn";
import { RadioGroup, RadioGroupItem } from "./radio-group";
import { Label } from "./label";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";
import { Info } from "lucide-react";
import { Checkbox } from "./checkbox";
import LoanUseDetailsView from "./loan-use-details";
import { loanUseOptions } from "@cxm/lib/loanapplications";
import { Slider } from "./slider";
import { LoanUse, LoanUseDetails } from "@cxm/client";
import { DatePicker } from "./datepicker";
import { loanTypes } from "@cxm/lib";
import { useFormContext } from "react-hook-form";

type FieldProps = {
  label: string;
  name: string;
  tooltip?: string;
  className?: string;
  disabled?: boolean;
  readOnly?: boolean;
  isFieldRequiredAndMissing?: (name: string, value: any) => boolean;
};

const DefaultTooltip = ({ message }: { message: string }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Tooltip open={hovered}>
      <TooltipTrigger asChild>
        <div
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          className={"ml-2 text-gray-500"}
        >
          <Info height={16} width={16} />
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-[300px]">
        {message}
      </TooltipContent>
    </Tooltip>
  );
};

export const NumberField = (
  { label, name, disabled, format, isFieldRequiredAndMissing }: FieldProps & {
    format?: (v: number) => string | undefined;
  },
) => {
  const [focused, setFocused] = useState(false);
  return (
    <FormField
      name={name}
      render={({ field }) => {
        return (
          <FormItem className="grid grid-cols-2 space-y-0">
            <FormLabel className="flex items-center">{label}</FormLabel>
            <FormControl>
              <Input
                className={(isFieldRequiredAndMissing &&
                  isFieldRequiredAndMissing(field.name, field.value))
                  ? "border-[1px] border-orange-500"
                  : ""}
                type="text"
                {...field}
                readOnly={disabled ?? field.disabled}
                onFocus={() => setFocused(true)}
                onBlur={() => {
                  field.onBlur();
                  setFocused(false);
                }}
                value={(!focused && format)
                  ? format(field.value)
                  : (field.value || "")}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "") {
                    field.onChange(0);
                  } else {
                    const num = parseInt(value);
                    field.onChange(isNaN(num) ? 0 : num);
                  }
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};

export const DateField = (
  { label, name, isFieldRequiredAndMissing }: FieldProps,
) => {
  return (
    <FormField
      name={name}
      render={({ field }) => {
        return (
          <>
            <FormItem className="grid grid-cols-2 space-y-0">
              <FormLabel className="flex items-center">
                {label}
              </FormLabel>
              <FormControl className="">
                <DatePicker
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e);
                  }}
                />
              </FormControl>
            </FormItem>
            <FormMessage className="text-right" />
          </>
        );
      }}
    />
  );
};

export const PhoneField = (
  { label, name, isFieldRequiredAndMissing }: FieldProps,
) => {
  return (
    <FormField
      name={name}
      render={({ field }) => {
        return (
          <>
            <FormItem className="grid grid-cols-2 space-y-0">
              <FormLabel className="flex items-center">
                {label}
              </FormLabel>
              <FormControl className="">
                <PhoneInput
                  disabled={field.disabled}
                  specialLabel=""
                  country="us"
                  inputProps={{
                    className:
                      "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ",
                  }}
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e);
                  }}
                />
              </FormControl>
            </FormItem>
            <FormMessage className="text-right" />
          </>
        );
      }}
    />
  );
};

export const LoanUseDetailsField = (
  { label, name, amount }: FieldProps & { amount: number },
) => {
  const purchaseUses: string[] = [
    LoanUse.EQUIPMENT,
    LoanUse.REAL_ESTATE,
    LoanUse.INVENTORY,
  ];
  return (
    <FormField
      name={name}
      render={({ field }) => {
        const useValue = (field.value as LoanUseDetails)?.loanUse as {
          [key: string]: number;
        } ?? {};
        const purchaseValue = (field.value as LoanUseDetails)?.purchaseUse as {
          [key: string]: number;
        } ?? {};
        const toPurchaseOptions = loanUseOptions.filter((x) =>
          purchaseUses.includes(x.value?.toString()) &&
          (useValue[x.value?.toString()] ?? 0 > 0)
        );
        return (
          <FormItem>
            <FormLabel>{label}</FormLabel>
            <FormControl>
              <div>
                <LoanUseDetailsView
                  loanAmount={amount}
                  loanUseDetails={field.value}
                />
                <div className="text-sm text-gray-500 mt-4">
                  Use the sliders below to allocate your use of funds to each
                  category.
                </div>
                <div className="flex flex-col text-sm space-y-1 mt-1 p-1">
                  {loanUseOptions.map((use) => (
                    <div className="grid grid-cols-2">
                      <div>{use.label}</div>
                      <Slider
                        color={use.color}
                        value={[
                          ((useValue && useValue[use.value.toString()]) ?? 0) *
                          100,
                        ]}
                        onValueChange={(value) => {
                          const newValue = (value[0] ?? 0) / 100;
                          const sum = Object.entries(useValue).filter((
                            [k, v],
                          ) => k != use.value && k != LoanUse.UNKNOWN).map((
                            [k, v],
                          ) => v).reduce(
                            (a, b) => a + b,
                            0,
                          );
                          const maxValue = 1 - sum;
                          if (newValue > 0 && newValue > maxValue) {
                            const newUse = { ...(useValue ?? {}) };
                            newUse[use.value.toString()] = Math.max(
                              0,
                              maxValue,
                            );
                            newUse[LoanUse.UNKNOWN] = 0;
                            field.onChange({
                              ...field.value,
                              loanUse: newUse,
                            });
                            return;
                          }
                          const newUse = { ...(useValue ?? {}) };
                          newUse[LoanUse.UNKNOWN] = 1 - sum;
                          newUse[use.value.toString()] = newValue;
                          field.onChange({
                            ...field.value,
                            loanUse: newUse,
                          });
                        }}
                        max={100}
                        min={0}
                        step={5}
                      />
                    </div>
                  ))}
                </div>
                <div className="flex flex-col text-sm space-y-1 mt-1 p-1">
                  {toPurchaseOptions.length > 0 && (
                    <div>
                      Enter the total value of the assets you are seeking to
                      purchase:
                    </div>
                  )}
                  {toPurchaseOptions.map((use) => {
                    return (
                      <div className="grid grid-cols-2 items-center">
                        <div className="text-sm">
                          Total Value of {use.label}{"  "}
                          you are seeking to purchase:
                        </div>
                        <MoneyInput
                          value={purchaseValue[use.value]}
                          onChange={(x) => {
                            const newPurchase = { ...(purchaseValue ?? {}) };
                            newPurchase[use.value.toString()] = Math.max(
                              0,
                              x,
                            );
                            field.onChange({
                              ...field.value,
                              purchaseUse: newPurchase,
                            });
                            return;
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </FormControl>
            <FormMessage className="text-right" />
          </FormItem>
        );
      }}
    />
  );
};

export const TextareaField = (
  { label, name, isFieldRequiredAndMissing }: FieldProps,
) => {
  return (
    <FormField
      name={name}
      render={({ field }) => {
        return (
          <FormItem>
            <FormLabel>{label}</FormLabel>
            <FormControl>
              <Textarea
                rows={5}
                className={(isFieldRequiredAndMissing &&
                  isFieldRequiredAndMissing(field.name, field.value))
                  ? " border-[1px] border-orange-500"
                  : ""}
                {...field}
                value={field.value || ""}
              />
            </FormControl>
            <FormMessage className="text-right" />
          </FormItem>
        );
      }}
    />
  );
};

export const SmallTextField = (
  {
    label,
    name,
    format,
    alwaysFormat,
    isFieldRequiredAndMissing,
    disabled,
    className,
    readOnly,
  }:
    & FieldProps
    & {
      alwaysFormat?: boolean;
      format?: (v: number) => string | undefined;
    },
) => {
  const [focused, setFocused] = useState(false);
  return (
    <FormField
      name={name}
      render={({ field }) => {
        return (
          <FormItem className={cn("grid grid-cols-2 space-y-0", className)}>
            <FormLabel className="flex items-center">{label}</FormLabel>
            <FormControl>
              <Input
                className={(isFieldRequiredAndMissing &&
                  isFieldRequiredAndMissing(field.name, field.value))
                  ? "border-[1px] border-orange-500"
                  : ""}
                {...field}
                onFocus={() =>
                  setFocused(true)}
                onBlur={() => {
                  field.onBlur();
                  setFocused(false);
                }}
                readOnly={readOnly}
                disabled={disabled || field.disabled}
                type="text"
                value={((!focused || alwaysFormat) && format)
                  ? format(field.value)
                  : (field.value || "")}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};

export const MoneyInput = (
  { value, onChange, className }: {
    value?: number;
    onChange: (v: number) => void;
    className?: string;
  },
) => {
  const [stringValue, setStringValue] = useState(
    value ? value.toString() : "",
  );
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if(value == 0 && stringValue == "-")
      return;
    if(value == 0 && stringValue == "")
      return;
    setStringValue(value?.toString() ?? "");
  }, [value]);

  return (
    <Input
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      className={"text-black " + (className ?? "")}
      type="text"
      value={isFocused ? stringValue : displayMoney(stringValue)}
      onChange={(e) => {
        const value = 
          (e.target as HTMLInputElement).value;
        if(value == ""){
          setStringValue("");
          onChange(0);
          return;
        }
        var stringVal = formatMoney(
          value,
        );
        setStringValue(
          stringVal,
        );
        if (stringVal == "-" || stringVal == "") {
          onChange(0);
        } else {
          onChange(parseInt(stringVal));
        }
      }}
    />
  );
};

export const MoneyField = (
  { label, name, disabled, overrideValue, isFieldRequiredAndMissing, tooltip }:
    & FieldProps
    & {
      dontAllowNegative?: boolean;
      overrideValue?: number;
    },
) => {
  return (
    <FormField
      name={name}
      render={({ field }) => {
        const [stringValue, setStringValue] = useState(
          field.value ? field.value as string : "",
        );

        useEffect(() => {
          setStringValue(field.value?.toString() ?? "");
        }, [field.value]);

        useEffect(() => {
          if (overrideValue) {
            setStringValue(overrideValue.toString());
          }
        }, [overrideValue]);

        return (
          <FormItem className="grid grid-cols-2 space-y-0">
            <FormLabel className="flex items-center">
              {label}
              {tooltip &&
                <DefaultTooltip message={tooltip} />}
            </FormLabel>
            <FormControl>
              <Input
                className={"text-black" +
                  ((isFieldRequiredAndMissing &&
                    isFieldRequiredAndMissing(field.name, field.value))
                    ? " border-orange-500 border-[1px]"
                    : "")}
                type="text"
                {...field}
                disabled={disabled ?? field.disabled}
                value={displayMoney(stringValue)}
                onChange={(e) => {
                  var stringVal = formatMoney(
                    (e.target as HTMLInputElement).value,
                  );
                  setStringValue(
                    stringVal,
                  );
                  if (stringVal == "-" || stringVal == "") {
                    field.onChange(0);
                  } else {
                    field.onChange(parseInt(stringVal));
                  }
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};

export const SwitchField = (
  { label, name, disabled }: FieldProps,
) => {
  return (
    <FormField
      name={name}
      render={({ field }) => {
        return (
          <FormItem
            className={"flex flex-row"}
          >
            <FormLabel className="flex items-center mr-4">{label}</FormLabel>
            <div className="ml-auto flex flex-row justify-end space-x-2 items-center">
              <div>{field.value == true ? "Yes" : "No"}</div>
              <FormControl>
                <Switch
                  {...field}
                  disabled={disabled ?? field.disabled}
                  checked={field.value}
                  value={field.value || false}
                  onCheckedChange={(v) => {
                    field.onChange(v);
                  }}
                />
              </FormControl>
            </div>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};

export const CheckboxField = (
  { label, name, disabled }: FieldProps,
) => {
  return (
    <FormField
      name={name}
      render={({ field }) => {
        return (
          <FormItem
            className={"flex flex-row"}
          >
            <FormLabel className="flex items-center mr-4">{label}</FormLabel>
            <div className="ml-auto flex flex-row justify-end space-x-2 items-center">
              <div>{field.value == true ? "Yes" : "No"}</div>
              <FormControl>
                <Checkbox
                  className="w-[22px] h-[22px]"
                  {...field}
                  disabled={disabled ?? field.disabled}
                  checked={field.value}
                  value={field.value || false}
                  onCheckedChange={(v) => {
                    field.onChange(v);
                  }}
                />
              </FormControl>
            </div>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};

export const MultiSelectField = (
  { label, name, options, disabled }: {
    label: string;
    name: string;
    options: { label: string; value: any }[];
  } & FieldProps,
) => {
  const fullOptions = [
    {
      label: "All",
      value: "select_all",
    },
    {
      label: "None",
      value: "select_none",
    },
    ...options,
  ];

  return (
    <FormField
      name={name}
      render={({ field }) => {
        return (
          <FormItem>
            <FormLabel>{label}</FormLabel>
            <FormControl>
              <Select
                isMulti
                menuShouldScrollIntoView={false}
                placeholder={field.disabled ? "None" : undefined}
                maxMenuHeight={320}
                styles={{
                  valueContainer: (styles) => ({
                    ...styles,
                    overflowY: "auto",
                    width: "100%",
                  }),
                }}
                options={fullOptions}
                isDisabled={field.disabled ?? disabled}
                {...field}
                value={options.filter((x) =>
                  field.value?.includes(x.value) ?? false
                )}
                onChange={(v) => {
                  let values = v as { label: string; value: string }[];
                  const requestedSelectAll =
                    values.find((x) => x.value == ("select_all")) != undefined;
                  if (
                    requestedSelectAll
                  ) {
                    values = options;
                  }
                  const requestedSelectNone = values.find((x) =>
                    x.value == ("select_none")
                  ) != undefined;
                  if (
                    requestedSelectNone
                  ) {
                    values = [];
                  }
                  field.onChange(
                    values.map((
                      x,
                    ) => x.value),
                  );
                }}
              />
            </FormControl>
            <FormMessage className="text-right" />
          </FormItem>
        );
      }}
    />
  );
};

export const SelectField = (
  {
    label,
    name,
    options,
    disabled,
    isFieldRequiredAndMissing,
    multi,
    horizontal,
  }: FieldProps & {
    options: {
      label: string;
      value: any;
    }[];
    multi?: boolean;
    horizontal?: boolean;
  },
) => {
  return (
    <FormField
      name={name}
      render={({ field }) => {
        return (
          <FormItem
            className={horizontal
              ? "grid grid-cols-1 gap-1"
              : "grid grid-cols-2"}
          >
            <FormLabel className="flex items-center">{label}</FormLabel>
            <FormControl>
              {field.disabled
                ? (
                  <div className="text-heavy">
                    {options.find((opt) => opt.value == field.value)?.label}
                  </div>
                )
                : (
                  <Select
                    isMulti={multi}
                    menuShouldScrollIntoView={false}
                    className={"text-black " +
                      ((isFieldRequiredAndMissing &&
                        isFieldRequiredAndMissing(field.name, field.value))
                        ? " rounded-md border-[1px] border-orange-500"
                        : " border-none")}
                    options={options}
                    {...field}
                    isDisabled={disabled ?? field.disabled}
                    value={!multi
                      ? (isObject(field.value)
                        ? field.value
                        : options.find((opt) => opt.value == field.value))
                      : field.value.map((x: any) =>
                        options.find((opt) => opt.value == x)
                      )}
                    onChange={(v) => {
                      field.onChange(
                        multi
                          ? (v as any).map((x: any) => x.value)
                          : (v as any).value,
                      );
                    }}
                  />
                )}
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};

export const PercentInput = (
  { value, onChange, className }: {
    value: number;
    onChange: (v: number) => void;
    className: string;
  },
) => {
  const [stringValue, setStringValue] = useState(value.toString());
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    setStringValue(value.toString());
  }, [value]);
  return (
    <Input
      className={className}
      onFocus={() => {
        setFocused(true);
      }}
      onBlur={() => {
        setFocused(false);
      }}
      type="text"
      value={focused ? value : (stringValue + "%")}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        var stringVal = (e.target as HTMLInputElement).value;
        setStringValue(
          stringVal,
        );
        if (stringVal == "-" || stringVal == "") {
          onChange(0);
        } else {
          onChange(parseFloat(stringVal));
        }
      }}
    />
  );
};

export const PercentField = (
  { label, name, disabled, isFieldRequiredAndMissing }: FieldProps & {
    dontAllowNegative?: boolean;
  },
) => {
  const [focused, setFocused] = useState(false);
  return (
    <FormField
      name={name}
      render={({ field }) => {
        const [stringValue, setStringValue] = useState(
          field.value ? field.value as string : "",
        );

        const isDisabled = disabled ?? field.disabled;

        return (
          <FormItem className="grid grid-cols-2 space-y-0">
            <FormLabel className="flex items-center">{label}</FormLabel>
            <FormControl>
              <Input
                className={"text-black" +
                  (isFieldRequiredAndMissing &&
                    isFieldRequiredAndMissing(field.name, field.value)
                    ? " border-[1px] border-orange-500"
                    : "")}
                type="text"
                {...field}
                readOnly={isDisabled}
                onFocus={() => {
                  setFocused(true);
                }}
                onBlur={() => {
                  field.onBlur();
                  setFocused(false);
                }}
                value={(focused && !isDisabled)
                  ? stringValue
                  : (stringValue == "" ? "0%" : stringValue + "%")}
                onChange={(e) => {
                  var stringVal = (e.target as HTMLInputElement).value;
                  setStringValue(
                    stringVal,
                  );
                  if (stringVal == "-" || stringVal == "") {
                    field.onChange(0);
                  } else {
                    field.onChange(parseFloat(stringVal));
                  }
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};

export const RadioField = (
  { label, name, disabled, options }: FieldProps & {
    options: { label: string; value: string }[];
  },
) => {
  return (
    <FormField
      name={name}
      render={({ field }) => {
        return (
          <FormItem
            className={"flex flex-col my-[40px] space-y-[20px]"}
          >
            <FormLabel className="flex items-center mr-4">{label}</FormLabel>
            <div className="flex flex-row  space-x-2 items-center justify-start">
              <FormControl>
                <RadioGroup
                  className="flex flex-row justify-center"
                  value={field.value}
                  onValueChange={(x) => field.onChange(x)}
                >
                  {options.map((x) => (
                    <div className="flex items-center space-x-2 text-lg">
                      <RadioGroupItem value={x.value} id={x.value} />
                      <Label htmlFor="option-one">{x.label}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </FormControl>
            </div>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};

export const LoanAskAmountsField = ({
  name,
  label,
  selectedLoanTypes,
}: {
  name: string;
  label: string;
  selectedLoanTypes: string[];
}) => {
  const { control, setValue, watch } = useFormContext();
  const currentAmounts = watch(name) || {};

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <div className="space-y-3">
            {selectedLoanTypes.map((loanTypeKey) => {
              const loanType = loanTypes.find((t) => t.key === loanTypeKey);
              return (
                <div key={loanTypeKey} className="flex items-center space-x-3">
                  <FormLabel className="min-w-[120px] text-sm">
                    {loanType?.fullName || loanTypeKey}:
                  </FormLabel>
                  <MoneyInput
                    value={currentAmounts[loanTypeKey] || 0}
                    onChange={(value) => {
                      const newAmounts = {
                        ...currentAmounts,
                        [loanTypeKey]: value,
                      };
                      setValue(name, newAmounts, { shouldValidate: true });
                    }}
                    className="flex-1"
                  />
                </div>
              );
            })}
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
